import React, { useEffect, useState } from "react";
import { Offer } from "interfaces/Offer";
import { OfferCurrencySale } from "interfaces/OfferCurrencySale";
import { getCachedConfiguration } from "utils/functions/configurationUtil";
import {
  TiDeviceDesktop,
  TiVendorAndroid,
  TiVendorApple,
  TiInfoLargeOutline,
} from "react-icons/ti";
import * as c from "./SandboxPage.module.css";

declare global {
  interface Window {
    dynataOffersSdk: any;
  }
}

type FetchType = "available" | "featured" | "progress" | "banners" | "currencySale";
type GoalMode = "none" | "extraAll" | "extraPurchaseOnly" | "extraStandardOnly";
type OsType = "Android" | "iOS" | "Desktop";
type Platform = "Android" | "iOS" | "Windows";

const platformIcons: Record<Platform, React.JSX.Element> = {
  Android: <TiVendorAndroid color="#A6D864" size={20} />,
  iOS: <TiVendorApple size={20} />,
  Windows: <TiDeviceDesktop size={18} color="#00a5ed" />,
};

const SandboxPage: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [currencySaleData, setCurrencySaleData] = useState<OfferCurrencySale | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sdkVersion, setSdkVersion] = useState(() => {
    return localStorage.getItem("sandbox_sdk_version") || "2.0.5";
  });

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [fetchType, setFetchType] = useState<FetchType>("available");
  const [goalMode, setGoalMode] = useState<GoalMode>("none");
  const [addAttributionWindows, setAddAttributionWindows] = useState(false);
  const [simulateCompletedPending, setSimulateCompletedPending] = useState(false);
  const [simulateRemainingTime, setSimulateRemainingTime] = useState(false);
  const [loadedVersion, setLoadedVersion] = useState<string>("");
  const [simulateBooster, setSimulateBooster] = useState(false);
  const [campaignMins, setCampaignMins] = useState<number | "">(60);
  const [publisherMins, setPublisherMins] = useState<number | "">(120);
  const [boosterMultiplier, setBoosterMultiplier] = useState<number | "">(2);

  const [filterOs, setFilterOs] = useState<OsType[]>([]);
  const [filterLimit, setFilterLimit] = useState<number | "">("");
  const [filterOffset, setFilterOffset] = useState<number | "">("");
  const [filterIncludeGoals, setFilterIncludeGoals] = useState<boolean>(true);

  const configuration = getCachedConfiguration();

  useEffect(() => {
    loadSdkScript(sdkVersion);
    return () => {
      const existing = document.querySelector("script[data-sdk-script]");
      if (existing) existing.remove();
    };
  }, []);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const loadSdkScript = (version: string) => {
    const existing = document.querySelector("script[data-sdk-script]");
    if (existing) existing.remove();

    if (window.dynataOffersSdk) window.dynataOffersSdk = undefined;

    const script = document.createElement("script");
    script.src = `https://sdk.surveyb.dev/${version}/sdk.min.js?t=${Date.now()}`;
    script.async = true;
    script.dataset.sdkScript = "true";

    script.onload = () => initSdk(version);
    script.onerror = () => {
      setError(`Failed to load SDK version ${version}`);
      showToast(`❌ Failed to load SDK v${version}`, "error");
    };

    document.body.appendChild(script);
  };

  const applyNewVersion = () => {
    localStorage.setItem("sandbox_sdk_version", sdkVersion);
    window.location.reload();
  };

  async function initSdk(version: string) {
    try {
      if (!window.dynataOffersSdk) return;

      await window.dynataOffersSdk.init({
        params:
          configuration?.params ||
          "UzF4YURpTGxtYWp6YkFSZ2tXWGsxeFlOcGpZTlN5Qks3d21lMGwyLzdrR3JyOVRzNUlKZkNtdHFnM2liY3o2QllMMWYrOHowT1E3alR3Z282QlhXNkV5QUJYd1ZUcEplYVJoMS9NZ3VLWU56UTl2L3pmMlRlOVhheGg0eURJOUlWVWdrSEkyYzhTcGc5dnVxRWJpY1d6UDV6R0ZmV05TdXJPSERnZVB0am5WQkJFT0U4cUFtQjVGMmphcVFYWlplbzVNT2NpbWQvSEdPREE0NG9VSGNHdG11S2dNaklPRTE2TS9RMTFpNENNRT0",
        appUserId: configuration?.publisher_app_uid,
      });

      setLoadedVersion(version);
      loadOffers(fetchType);
      showToast(`✅ SDK v${version} loaded`, "success");
    } catch (err: any) {
      setError(err.message);
      showToast(`❌ SDK initialization failed`, "error");
    }
  }

  async function loadOffers(type: FetchType) {
    if (!window.dynataOffersSdk) return;
    setLoading(true);
    setError(null);
    setOffers([]);
    setCurrencySaleData(null);

    try {
      if (type === "currencySale") {
        try {
          const sale = await window.dynataOffersSdk.fetchCurrencySale();
          setCurrencySaleData(sale || null);
        } catch (err: any) {
          if (err.message?.includes("JSON") || err.status === 204) {
            console.warn("Caught expected 204 No Content for Currency Sale:", err);
            setCurrencySaleData(null);
          } else {
            throw err;
          }
        }
        setLoading(false);
        return;
      }

      let data: Offer[] = [];
      const baseParams: any = {};
      if (filterOs.length > 0) baseParams.os = filterOs;

      const fullParams = { ...baseParams };
      if (filterLimit !== "") fullParams.limit = Number(filterLimit);
      if (filterOffset !== "") fullParams.offset = Number(filterOffset);
      fullParams.includeGoals = filterIncludeGoals;

      switch (type) {
        case "available":
          data = await window.dynataOffersSdk.fetchOffers(
            Object.keys(fullParams).length > 0 ? fullParams : undefined,
          );
          break;
        case "featured":
          data = await window.dynataOffersSdk.fetchFeaturedOffers(
            Object.keys(fullParams).length > 0 ? fullParams : undefined,
          );
          break;
        case "progress":
          data = await window.dynataOffersSdk.fetchOffersInProgress();
          break;
        case "banners":
          data = await window.dynataOffersSdk.fetchBannerAds(
            Object.keys(baseParams).length > 0 ? baseParams : undefined,
          );
          break;
        default:
          console.warn(`Unhandled fetch type: ${type}`);
          data = [];
      }
      setOffers(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const handleFetchTypeChange = (newType: FetchType) => {
    setFetchType(newType);
    setGoalMode("none");
    setAddAttributionWindows(false);
    setSimulateBooster(false);

    if (newType !== "progress") {
      setSimulateCompletedPending(false);
      setSimulateRemainingTime(false);
    }
    setOffers([]);
    setCurrencySaleData(null);
    setError(null);
    loadOffers(newType);
  };

  const handleGoalModeChange = (mode: GoalMode) => {
    setGoalMode((prev) => (prev === mode ? "none" : mode));
    showToast(`Goal mode: ${mode === "none" ? "off" : mode}`, "success");
  };

  const handleOsToggle = (os: OsType) => {
    setFilterOs((prev) => (prev.includes(os) ? prev.filter((item) => item !== os) : [...prev, os]));
  };

  const viewOffer = (offer: Offer, isInProgress = false) => {
    window.dynataOffersSdk.renderOfferModal(offer, isInProgress, {
      __internalGoalMode: goalMode,
      __addAttributionWindows: addAttributionWindows,
      __boosterSimulation: {
        enabled: simulateBooster,
        campaignMinutes: campaignMins,
        publisherMinutes: publisherMins,
        multiplier: boosterMultiplier === "" ? 2 : boosterMultiplier,
      },
      __simulateCompletedPendingGoals: simulateCompletedPending,
      __simulateRemainingTime: simulateRemainingTime,
    });
  };

  const areCheckboxControlsDisabled = fetchType === "currencySale";

  return (
    <div className={c.sandboxContainer}>
      {toast && (
        <div
          className={`${c.toast} ${c.show} ${toast.type === "error" ? c.toastError : c.toastSuccess}`}
        >
          {toast.message}
        </div>
      )}

      <div className={c.testControls}>
        <div className={c.testControlsHeader}>🧪 Testing Controls</div>

        <div className={c.versionControl}>
          <label htmlFor="sdkVersion">SDK Version:</label>
          <input
            id="sdkVersion"
            className={c.versionInput}
            type="text"
            value={sdkVersion}
            onChange={(e) => setSdkVersion(e.target.value)}
          />
          <button
            className={c.applyButton}
            onClick={applyNewVersion}
            disabled={sdkVersion === loadedVersion || !sdkVersion}
            style={{
              opacity: sdkVersion === loadedVersion ? 0.5 : 1,
              cursor: sdkVersion === loadedVersion ? "not-allowed" : "pointer",
            }}
          >
            {sdkVersion === loadedVersion ? "Applied" : "Apply & Reload"}
          </button>
        </div>

        <div className={c.fetchType}>
          {(["available", "featured", "progress", "banners", "currencySale"] as FetchType[]).map(
            (type) => (
              <label key={type}>
                <input
                  type="radio"
                  name="fetchType"
                  value={type}
                  checked={fetchType === type}
                  onChange={() => handleFetchTypeChange(type)}
                />
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </label>
            ),
          )}
        </div>

        <div className={c.filterSection}>
          <div className={c.filterHeader}>Fetch Filters</div>

          {fetchType === "progress" ? (
            <div className={c.filterInfo}>No filters available for In Progress offers.</div>
          ) : (
            <>
              <div className={c.filterRow}>
                <span style={{ fontWeight: 500 }}>OS:</span>
                {(["Android", "iOS", "Desktop"] as OsType[]).map((os) => (
                  <label key={os} className={c.filterLabel}>
                    <input
                      type="checkbox"
                      checked={filterOs.includes(os)}
                      onChange={() => handleOsToggle(os)}
                      disabled={areCheckboxControlsDisabled}
                    />
                    {os}
                  </label>
                ))}
              </div>

              {(fetchType === "available" || fetchType === "featured") && (
                <div className={c.filterRow}>
                  <label className={c.filterLabel}>
                    Limit:
                    <input
                      type="number"
                      className={c.filterInput}
                      value={filterLimit}
                      onChange={(e) =>
                        setFilterLimit(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder="e.g. 20"
                    />
                  </label>
                  <label className={c.filterLabel}>
                    Offset:
                    <input
                      type="number"
                      className={c.filterInput}
                      value={filterOffset}
                      onChange={(e) =>
                        setFilterOffset(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      placeholder="e.g. 0"
                    />
                  </label>
                  <label className={c.filterLabel}>
                    <input
                      type="checkbox"
                      checked={filterIncludeGoals}
                      onChange={(e) => setFilterIncludeGoals(e.target.checked)}
                      disabled={areCheckboxControlsDisabled}
                    />
                    Include Goals
                  </label>
                </div>
              )}

              <button
                className={`${c.applyButton} ${c.applyFiltersBtn}`}
                onClick={() => loadOffers(fetchType)}
              >
                Apply Filters & Fetch
              </button>
            </>
          )}
        </div>

        <div className={c.goalMode}>
          <div className={c.goalModeInfo}>
            ⚠️ These goal modification options are only supported in SDK version{" "}
            <strong>2.0+</strong>.
          </div>
          <span>Goal Modification Mode:</span>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraAll"}
              onChange={() => handleGoalModeChange("extraAll")}
              disabled={areCheckboxControlsDisabled}
            />
            Add to All
          </label>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraPurchaseOnly"}
              onChange={() => handleGoalModeChange("extraPurchaseOnly")}
              disabled={areCheckboxControlsDisabled}
            />
            Purchase Only
          </label>
          <label>
            <input
              type="checkbox"
              checked={goalMode === "extraStandardOnly"}
              onChange={() => handleGoalModeChange("extraStandardOnly")}
              disabled={areCheckboxControlsDisabled}
            />
            Standard Only
          </label>
        </div>

        {goalMode !== "none" && (
          <div className={c.attributionSection}>
            {fetchType === "available" ? (
              <label className={c.checkboxItem}>
                <input
                  type="checkbox"
                  checked={addAttributionWindows}
                  onChange={() => setAddAttributionWindows(!addAttributionWindows)}
                  disabled={areCheckboxControlsDisabled}
                />
                Add Attribution Windows (0m → 3d range)
              </label>
            ) : (
              <div className={c.attributionInfo}>
                ℹ️ Attribution windows are only available for <strong>Available</strong> offers.
              </div>
            )}

            {/* Booster Simulation Area */}
            {(fetchType === "available" || fetchType === "progress") && (
              <div
                style={{
                  marginTop: "12px",
                  background: "rgba(0,0,0,0.03)",
                  padding: "10px",
                  borderRadius: "8px",
                }}
              >
                <label className={c.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={simulateBooster}
                    onChange={() => setSimulateBooster(!simulateBooster)}
                    disabled={areCheckboxControlsDisabled}
                  />
                  <strong>Simulate Booster Banner (Currency Sale Multiplier)</strong>
                </label>

                {simulateBooster && (
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      paddingLeft: "24px",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        maxWidth: "320px",
                        fontSize: "14px",
                      }}
                    >
                      Multiplier (e.g., 2, 3):
                      <input
                        type="number"
                        value={boosterMultiplier}
                        onChange={(e) =>
                          setBoosterMultiplier(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        disabled={areCheckboxControlsDisabled}
                        style={{ width: "80px" }}
                      />
                    </label>
                    {/* --------------------------- */}
                    <label
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        maxWidth: "320px",
                        fontSize: "14px",
                      }}
                    >
                      Campaign Sale Ends In (mins):
                      <input
                        type="number"
                        value={campaignMins}
                        onChange={(e) =>
                          setCampaignMins(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        disabled={areCheckboxControlsDisabled}
                        style={{ width: "80px" }}
                      />
                    </label>
                    <label
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        maxWidth: "320px",
                        fontSize: "14px",
                      }}
                    >
                      Publisher Sale Ends In (mins):
                      <input
                        type="number"
                        value={publisherMins}
                        onChange={(e) =>
                          setPublisherMins(e.target.value === "" ? "" : Number(e.target.value))
                        }
                        disabled={areCheckboxControlsDisabled}
                        style={{ width: "80px" }}
                      />
                    </label>
                    <div
                      className={c.attributionInfo}
                      style={{ fontSize: "12px", marginTop: "4px" }}
                    >
                      ℹ️ To test the Booster priority logic, the banner will automatically display
                      the sale that expires <strong>earlier</strong>. Leave an input blank to test
                      with only one sale source.
                    </div>
                  </div>
                )}
              </div>
            )}

            {fetchType === "progress" && (
              <div style={{ marginTop: "12px" }}>
                <label className={c.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={simulateCompletedPending}
                    onChange={() => setSimulateCompletedPending(!simulateCompletedPending)}
                    disabled={areCheckboxControlsDisabled}
                  />
                  Simulate Completed / Pending Goals (first 4)
                </label>

                <label className={c.checkboxItem}>
                  <input
                    type="checkbox"
                    checked={simulateRemainingTime}
                    onChange={() => setSimulateRemainingTime((v) => !v)}
                    disabled={areCheckboxControlsDisabled}
                  />
                  Simulate Remaining Time (completeByUtc) outcomes
                </label>
              </div>
            )}
          </div>
        )}
      </div>

      <h1>Offers SDK Demo</h1>

      {loading && <div className={c.loading}>Loading...</div>}
      {error && <div className={c.error}>Error: {error}</div>}

      {/* --- RENDER CONTENT SECTION --- */}
      {fetchType === "currencySale" ? (
        !loading && currencySaleData ? (
          // Active Sale State
          <div
            style={{
              maxWidth: "400px",
              padding: "20px",
              background: "#fdf8e4",
              border: "1px solid #f2e2a4",
              borderRadius: "12px",
              marginTop: "20px",
            }}
          >
            <h3
              style={{
                margin: "0 0 10px 0",
                color: "#b48c08",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "24px" }}>🔥</span> {currencySaleData.multiplier}x Multiplier
              Active
            </h3>
            <p style={{ margin: "0 0 10px 0", color: "#666", fontSize: "14px" }}>
              {currencySaleData.description}
            </p>
            <div style={{ fontSize: "13px", color: "#444" }}>
              <div>
                <strong>Starts:</strong> {new Date(currencySaleData.startOn).toLocaleString()}
              </div>
              <div>
                <strong>Ends:</strong> {new Date(currencySaleData.endOn).toLocaleString()}
              </div>
            </div>
          </div>
        ) : !loading && !currencySaleData ? (
          // 204 No Content Empty State
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              background: "#fafafa",
              border: "2px dashed #ddd",
              borderRadius: "12px",
              marginTop: "20px",
              maxWidth: "500px",
              margin: "20px auto",
            }}
          >
            <TiInfoLargeOutline size={48} color="#aaa" style={{ marginBottom: "12px" }} />
            <h3 style={{ color: "#555", margin: "0 0 8px 0" }}>No Active Currency Sale</h3>
            <p style={{ color: "#888", fontSize: "14px", margin: 0, lineHeight: "1.5" }}>
              The API returned a 204 No Content status.
              <br />
              There are currently no active currency sales running for this configuration.
            </p>
          </div>
        ) : null
      ) : // Standard Offers/Banners Grid
      !loading && offers.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#888" }}>
          No offers found for the current filters.
        </div>
      ) : (
        <div className={c.offersGrid}>
          {offers.map((offer) => (
            <div
              key={offer.id}
              className={c.offerCard}
              onClick={() => viewOffer(offer, fetchType === "progress")}
            >
              <img src={offer.thumbnailUrl} alt={offer.title} className={c.offerImage} />
              <div className={c.offerContent}>
                <div className={c.offerTitle}>{offer.title}</div>
                {offer.operatingSystems !== null && offer.operatingSystems.length !== 0 && (
                  <div className={c.icons}>
                    {offer.operatingSystems.map((platform) => (
                      <span key={platform}>{platformIcons[platform as Platform] || null}</span>
                    ))}
                  </div>
                )}
                <div className={c.offerReward}>Earn up to {offer.rewardIac}</div>
                <div className={c.offerDetails}>
                  {(offer.description || []).slice(0, 1).join(" ")}
                </div>
                <button className={c.viewButton}>View Details</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SandboxPage;
